// posthog-react-native → posthog-js adapter.
//
// src/services/analytics/analytics.ts stays untouched: it constructs
// `new PostHog(key, { host, flushAt, flushInterval, captureAppLifecycleEvents })`
// and calls capture / screen / identify / reset. This maps those onto the
// browser SDK with the same privacy posture as the phone: no autocapture,
// no automatic pageviews (the app reports screens itself), no session
// recording, no cookies (identity kept in localStorage only).

import posthog from 'posthog-js';

type Props = Record<string, unknown> | undefined;

interface Options {
  host?: string;
  flushAt?: number;
  flushInterval?: number;
  captureAppLifecycleEvents?: boolean;
}

export default class PostHog {
  private ready = false;

  constructor(apiKey: string, options: Options = {}) {
    if (!apiKey) return;
    posthog.init(apiKey, {
      api_host: options.host ?? 'https://us.i.posthog.com',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      disable_surveys: true,
      // Nothing loads from PostHog's asset host: no remote config script,
      // no lazily fetched extensions (recorder, surveys, toolbar). The
      // Content-Security-Policy (web/public/_headers) allows only the
      // ingestion host, and this keeps the SDK from trying.
      disable_external_dependency_loading: true,
      advanced_disable_flags: true,
      persistence: 'localStorage',
      request_batching: true,
      loaded: () => {
        this.ready = true;
      },
    });
    // Every web event says which surface and build it came from, so the
    // dashboards that mirror the phone's events can split by platform.
    posthog.register({
      platform: 'web',
      app_release: process.env.FK_WEB_RELEASE ?? 'finalkey-web@dev',
      app_environment: process.env.FK_WEB_ENV ?? 'development',
    });
    this.ready = true;
    if (options.captureAppLifecycleEvents) {
      posthog.capture('Application Opened');
    }
  }

  capture(event: string, properties?: Props): void {
    if (!this.ready) return;
    posthog.capture(event, properties);
  }

  screen(name: string, properties?: Props): void {
    if (!this.ready) return;
    posthog.capture('$screen', { $screen_name: name, ...(properties ?? {}) });
  }

  identify(distinctId: string, properties?: Props): void {
    if (!this.ready) return;
    posthog.identify(distinctId, properties);
  }

  alias(alias: string): void {
    if (!this.ready) return;
    posthog.alias(alias);
  }

  register(properties: Record<string, unknown>): void {
    if (!this.ready) return;
    posthog.register(properties);
  }

  reset(): void {
    if (!this.ready) return;
    posthog.reset();
  }

  optIn(): void {
    posthog.opt_in_capturing();
  }

  optOut(): void {
    posthog.opt_out_capturing();
  }

  async flush(): Promise<void> {
    // posthog-js batches and flushes on its own schedule and on unload.
  }

  async shutdown(): Promise<void> {
    this.ready = false;
  }

  isFeatureEnabled(flag: string): boolean | undefined {
    return posthog.isFeatureEnabled(flag);
  }

  getFeatureFlag(flag: string): string | boolean | undefined {
    return posthog.getFeatureFlag(flag);
  }
}
